import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteRenderOptionState,
  Box,
  CircularProgress,
  SxProps
} from "@mui/material";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { Component, HTMLAttributes, SyntheticEvent } from "react";
import DropIcon from "../../resources/images/icons-chevron-double.svg";
import RemoveIcon from "../../resources/images/icons-remove.svg";
import { TextField } from "../TextField";

interface CourseSelectorProps {
  label?: string;
  allowMultiple?: boolean;
  labelKey?: string;
  onChange?: (e: SyntheticEvent<Element, Event>, course: any) => void;
  onSearch?: (value: string) => void;
  onBlur?: (e: SyntheticEvent<Element, Event>) => void;
  options: any[];
  value?: any;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  isLoading?: boolean;
  sx?: SxProps;
  placeholder?: string;
  translate?: (key: string) => string;
}

export class CourseSelector extends Component<CourseSelectorProps, any> {
  static defaultProps = {
    label: "Training",
    allowMultiple: false,
    labelKey: "name"
  };

  constructor(props: CourseSelectorProps) {
    super(props);
  }

  onChange(e: SyntheticEvent<Element, Event>, course: any, reason: AutocompleteChangeReason) {
    const { onChange } = this.props;

    if (reason === "removeOption" && e?.type !== "click") return;
    onChange?.(e, course);
  }

  onInputChange(e: any, value: string, reason: AutocompleteInputChangeReason) {
    const { onSearch } = this.props;

    if (reason === "input") {
      onSearch?.(value);
    }
  }

  onBlur(e: SyntheticEvent<Element, Event>) {
    const { onBlur } = this.props;

    if (onBlur) onBlur(e);
  }

  renderOption(
    props: HTMLAttributes<HTMLLIElement> & {
      key: any;
    },
    option: any,
    state: AutocompleteRenderOptionState
  ) {
    const { labelKey } = this.props;

    const matches = match(option[labelKey || "name"], state.inputValue, {
      insideWords: true,
      requireMatchAll: false
    });
    const parts = parse(option[labelKey || "name"], matches);

    return (
      <li {...props} key={option.value}>
        {parts.map((part, index) => (
          <span
            key={index}
            className={
              "klayo-selector_itempart" +
              (part.highlight === true ? " klayo-selector_highlight" : "")
            }
          >
            {part.text}
          </span>
        ))}
      </li>
    );
  }

  render() {
    const {
      label,
      allowMultiple,
      sx,
      placeholder,
      options,
      value,
      error,
      errorMessage,
      disabled,
      isLoading,
      labelKey
    } = this.props;

    return (
      <Autocomplete
        id='klayo-attributeselector'
        className='klayo-attributeselector klayo-selector'
        options={options}
        value={value}
        getOptionLabel={(option) => {
          return option[labelKey || "name"];
        }}
        renderOption={this.renderOption.bind(this)}
        disabled={disabled}
        multiple={allowMultiple}
        popupIcon={<DropIcon />}
        clearIcon={<RemoveIcon />}
        ChipProps={{
          deleteIcon: <RemoveIcon />
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              disabled={disabled}
              error={error}
              helperText={errorMessage}
              sx={{ width: "100%" }}
              endAdornment={params.InputProps.endAdornment}
            />
          );
        }}
        onChange={this.onChange.bind(this)}
        onInputChange={this.onInputChange.bind(this)}
        onBlur={this.onBlur.bind(this)}
        sx={sx}
        loading={isLoading}
        loadingText={
          <Box display='flex' justifyContent={"center"} alignItems={"center"}>
            <CircularProgress size={20} />
          </Box>
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={this.props.translate?.("common.noOptions") || "No options"}
      />
    );
  }
}
